import React from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useRoute } from '@react-navigation/native';

import { GET_MY_UNAVAILABILITIES } from '../../../common/graphql/people/roster';
import {
	formatLeaveBalanceUnits,
	LEAVE_BALANCE_LABELS,
} from '../../../common/utils/people/leaveBalance';
import {
	buildShiftEndTimeOptions,
	buildShiftStartTimeOptions,
	formatRosterDate,
	SELF_SERVICE_UNAVAILABILITY_NOTICE_MESSAGE,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import Text from '../../components/text/Text';
import { TextInput } from '../../components/input';
import { SwitchWithLabel } from '../../components';
import {
	ActionDock,
	Card,
	EmptyState,
	Screen,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import DateSelect from './components/DateSelect';
import OptionSelect from './components/OptionSelect';
import { createMyLeaveRequestViewStyles } from './MyLeaveRequestView.styles';
import RepeatUnavailabilityFields from './RepeatUnavailabilityFields';
import { ROSTER_DATE_FORMAT } from './rosterHelpers';
import useLeaveRequestForm from './useLeaveRequestForm';
import useMyRosterProfile from './useMyRosterProfile';

const START_TIME_OPTIONS = buildShiftStartTimeOptions();
const END_TIME_OPTIONS = buildShiftEndTimeOptions();

// Form body; mounted once the edited entry (if any) is known so the fields start from it.
const LeaveRequestForm = ({ kind, editingEntry }) => {
	const styles = useThemedStyles(createMyLeaveRequestViewStyles);
	const {
		isLeaveForm,
		selfServiceCutoffDate,
		leaveType,
		setLeaveType,
		leaveTypeOptions,
		leaveBalances,
		startDate,
		onChangeStartDate,
		endDate,
		setEndDate,
		allDay,
		setAllDay,
		startMinutes,
		setStartMinutes,
		endMinutes,
		setEndMinutes,
		notes,
		setNotes,
		canRepeatUnavailability,
		repeatEnabled,
		setRepeatEnabled,
		repeatUntilDate,
		setRepeatUntilDate,
		repeatIntervalWeeks,
		setRepeatIntervalWeeks,
		repeatWeekdays,
		toggleRepeatWeekday,
		repeatPreviewDates,
		isShortNoticeUnavailability,
		minimumEntryDate,
		canSubmitEntry,
		isSaving,
		formError,
		submitEntry,
	} = useLeaveRequestForm({ kind, editingEntry });

	let submitLabel = isLeaveForm ? 'Submit request' : 'Add';
	if (editingEntry) {
		submitLabel = 'Save';
	}
	else if (!isLeaveForm && repeatEnabled && canRepeatUnavailability) {
		submitLabel = 'Add entries';
	}
	let heading = isLeaveForm ? 'Request leave' : 'Add unavailability';
	if (editingEntry) {
		heading = isLeaveForm ? 'Edit leave' : 'Edit unavailability';
	}

	return (
		<Screen
			backLabel="Leave"
			heading={heading}
			dock={(
				<ActionDock
					action={{
						label: submitLabel,
						onPress: submitEntry,
						loading: isSaving,
						disabled: !canSubmitEntry,
					}}
				/>
			)}
		>
			{isLeaveForm ? (
				<View style={styles.group}>
					<OptionSelect
						label="Leave type"
						options={leaveTypeOptions}
						value={leaveType}
						onChange={setLeaveType}
					/>
					{leaveBalances.map(balance => (
						<Text key={balance.leaveCategoryCode || balance.leaveName} style={styles.balanceText}>
							{`${formatLeaveBalanceUnits(balance)} ${String(LEAVE_BALANCE_LABELS[balance.leaveCategoryCode]).toLowerCase()} available`}
						</Text>
					))}
					{leaveBalances.length ? (
						<Text style={styles.footnote}>
							From Xero payroll. May not include leave approved since the last pay run.
						</Text>
					) : null}
				</View>
			) : null}

			<View style={styles.row}>
				<DateSelect
					label="First day"
					value={startDate}
					onChange={onChangeStartDate}
					minimumDate={minimumEntryDate}
					style={styles.rowItem}
				/>
				<DateSelect
					label="Last day"
					value={endDate}
					onChange={setEndDate}
					minimumDate={startDate || minimumEntryDate}
					style={styles.rowItem}
				/>
			</View>

			{!isLeaveForm ? (
				<Card tone="warning" padded={false} style={styles.notice}>
					<Text style={styles.noticeText}>
						{isShortNoticeUnavailability
							? `${SELF_SERVICE_UNAVAILABILITY_NOTICE_MESSAGE} Earliest self-service date is ${formatRosterDate(selfServiceCutoffDate, ROSTER_DATE_FORMAT)}.`
							: 'Self-service unavailability needs at least one week\'s notice. Short-notice dates must be handled directly with your manager.'}
					</Text>
				</Card>
			) : null}

			{canRepeatUnavailability ? (
				<RepeatUnavailabilityFields
					repeatEnabled={repeatEnabled}
					setRepeatEnabled={setRepeatEnabled}
					repeatUntilDate={repeatUntilDate}
					setRepeatUntilDate={setRepeatUntilDate}
					repeatIntervalWeeks={repeatIntervalWeeks}
					setRepeatIntervalWeeks={setRepeatIntervalWeeks}
					repeatWeekdays={repeatWeekdays}
					toggleRepeatWeekday={toggleRepeatWeekday}
					repeatPreviewDates={repeatPreviewDates}
					minimumDate={startDate || minimumEntryDate}
				/>
			) : null}

			<Card padded={false} style={styles.switchCard}>
				<SwitchWithLabel
					label="All day"
					value={allDay}
					onValueChange={setAllDay}
				/>
			</Card>
			{!allDay ? (
				<View style={styles.row}>
					<OptionSelect
						label="From"
						options={START_TIME_OPTIONS}
						value={startMinutes}
						onChange={setStartMinutes}
						style={styles.rowItem}
					/>
					<OptionSelect
						label="To"
						options={END_TIME_OPTIONS}
						value={endMinutes}
						onChange={setEndMinutes}
						style={styles.rowItem}
					/>
				</View>
			) : null}

			<View style={styles.group}>
				<Text style={styles.label}>Notes</Text>
				<TextInput
					value={notes}
					onChange={setNotes}
					placeholder={isLeaveForm ? 'Reason for the leave request' : 'Optional notes'}
					multiline={true}
					accessibilityLabel="Notes"
					style={styles.notesInput}
				/>
			</View>
			{isLeaveForm ? (
				<Text style={styles.footnote}>
					Leave requests may be approved automatically under your manager’s leave setting.
				</Text>
			) : null}
			{formError ? <Text style={styles.errorText}>{formError}</Text> : null}
		</Screen>
	);
};

// Request leave / add unavailability, or edit one (route params: kind, or entryId).
const MyLeaveRequestView = () => {
	const { params = {} } = useRoute();
	const { isAVC } = useUserContext();
	const { isLoading: isProfileLoading } = useMyRosterProfile();
	const { data, loading } = useQuery(GET_MY_UNAVAILABILITIES, {
		skip: !isAVC || !params.entryId,
		fetchPolicy: 'cache-first',
	});
	const editingEntry = params.entryId
		? (data?.getMyUnavailabilities || []).find(entry => entry.id === params.entryId) || null
		: null;

	if (isProfileLoading || (params.entryId && loading && !data)) {
		return <Screen backLabel="Leave" isLoading={true} />;
	}

	if (params.entryId && !editingEntry) {
		return (
			<Screen backLabel="Leave" heading="Edit request">
				<Card>
					<EmptyState
						icon="alert-circle"
						title="Request not found"
						message="This request may have been removed. Go back to Leave to see your requests."
					/>
				</Card>
			</Screen>
		);
	}

	const kind = editingEntry ? Number(editingEntry.kind) : Number(params.kind);

	return <LeaveRequestForm key={editingEntry?.id || 'new'} kind={kind} editingEntry={editingEntry} />;
};

export default MyLeaveRequestView;
